import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { JOB_STATUSES, RPC_TYPES } from 'utils';
import {
  ThreeDRpcIcon,
  TwoDRpcIcon,
  WarningOutlined,
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined,
  Reload,
    RefreshOutlined,
} from 'components/icons';
import { SpinAnimation } from 'components/generic';
import { VerticalSpacer, HorizontalSpacer } from 'components/layout';
import { ButtonsContainer, IconButton } from './styles';
import { DownloadFormatsModal } from './DownloadFormatsModal';
import { FovConfirmation } from 'components/notifications';
import { Body } from 'components/typography';
import { hasAccess } from 'navigation/navigationHelpers';
import { withTheme } from 'styled-components';
import constants from 'constants/index';

const handleStatus = (status, jobType, scaling, isCardView) => {
  if (status === JOB_STATUSES.PROCESSING || status === JOB_STATUSES.QUEUED) {
    return (
      <SpinAnimation duration={3000} absolutePosition={isCardView}>
        <Reload
          height={scaling.jobStatus.processIcon.height}
          width={scaling.jobStatus.processIcon.width}
        />
      </SpinAnimation>
    );
  }

  if (status === JOB_STATUSES.FAILED) {
    return (
      <WarningOutlined
        width={scaling.jobStatus.width}
        height={scaling.jobStatus.height}
      />
    );
  }

  if (status === JOB_STATUSES.COMPLETED) {
    return (
      <ThreeDRpcIcon
        width={scaling.jobStatus.width}
        height={scaling.jobStatus.height}
      />
    );
  }

  if (status === JOB_STATUSES.COMPLETED && jobType === RPC_TYPES.png) {
    return (
      <TwoDRpcIcon
        width={scaling.jobStatus.width}
        height={scaling.jobStatus.height}
      />
    );
  }
};

const ModelActions = withTheme(props => {
  const {
    theme,
    scaling,
    rpc_guid,
    isCardView,
    handlers,
    title,
    job_type,
    formats,
    purchased,
    job_status,
  } = props;

  const userDataStore = useSelector(state => state.userData);
  const onCreators = location.pathname.includes('creators');
  const onTeams = location.pathname.includes('team-models');
  const onWelcome = location.pathname.includes('welcome');
  const onMyFiles = location.pathname.includes('my-files');
  const onReadonlyPage = onCreators || onWelcome;
  const temporaryRole = userDataStore.temporaryRole;
  const userRoles = temporaryRole !== 'default' ? [ temporaryRole ] : userDataStore?.data?.roles;
  const userId = userDataStore?.data?.userId;
  const { setIsDeleting, onDelete, onDownload, onReprocess, setIsReprocessing, onPublish } = handlers;

  const [ confirmDelete, setConfirmDelete ] = useState(false);
  const [ confirmReprocess, setConfirmReprocess ] = useState(false);
  const [ downloadModal, setDownloadModal ] = useState(false);

  const selectedTheme = userDataStore.userPreferences.theme;
  const navigate = useNavigate();
  const light = constants.THEME.LIGHT;
  const enabledIconColor = selectedTheme === light ? 'darkGrey' : 'lightGrey';
  const iconColor = rpc_guid ? enabledIconColor : 'mediumGrey';
  const errorColor = theme.colors[selectedTheme].error;
  const errorHoveredColor = theme.hexToRgba(errorColor, 0.8);
  const isImage = job_type === RPC_TYPES.png;
  const showEditButton = !isImage && rpc_guid && userId && !onWelcome
    && formats['GLB'] && formats['GLB'] === 'AVAILABLE';

  const handleDeleting = e => {
    setIsDeleting(true);
    onDelete(e);
    e.stopPropagation();
  };

    const handleReprocessing = e => {
        setIsReprocessing(true);
        onReprocess(e);
        e.stopPropagation();
    };

  const createFormatsModal = () => {
    const handleFormatDownload = (format, callbacks) => {
      onDownload(rpc_guid, format, callbacks?.onComplete);
    };

    const handleFormatPublish = (format, callbacks) => {
      onPublish?.(rpc_guid, format, callbacks);
    };

    return (
      <DownloadFormatsModal
        visible={downloadModal}
        formats={formats}
        rpcGuid={rpc_guid}
        canPublish={onMyFiles}
        onClose={() => setDownloadModal(false)}
        onDownload={handleFormatDownload}
        onPublish={handleFormatPublish}
      />
    );
  };

  return (
    <>
      <ButtonsContainer
        isCardView={isCardView}
        height={scaling.buttonContainer.height}
      >
        {showEditButton && (
          <>
            <IconButton
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={e => {
                e.stopPropagation();
                navigate(`/${onCreators ? 'creator-' : ''}${onTeams ? 'team-' : ''}models/${rpc_guid}`);
              }}
              disabled={!rpc_guid}
              width={scaling.icons.width}
              height={scaling.icons.height}
              icon={
                <EditOutlined
                  color={theme.colors[selectedTheme][iconColor]}
                  hoverColor={rpc_guid && theme.colors[selectedTheme].accent}
                  width={scaling.icons.width}
                  height={scaling.icons.height}
                />
              }
            />

            {isCardView ? (
              <VerticalSpacer size={scaling.icons.spacerBetween} />
            ) : (
              <HorizontalSpacer size={scaling.icons.spacerBetween * 3} />
            )}
          </>
        )}

        {rpc_guid && userId && !onWelcome && (
          <>
            <IconButton
              theme={theme}
              $selectedTheme={selectedTheme}
              width={scaling.icons.width}
              height={scaling.icons.height}
              disabled={!rpc_guid}
              onClick={e => {
                e.stopPropagation();
                setDownloadModal(true);
              }}
              icon={
                <DownloadOutlined
                  color={theme.colors[selectedTheme][iconColor]}
                  hoverColor={rpc_guid && theme.colors[selectedTheme].accent}
                  width={scaling.icons.width}
                  height={scaling.icons.height}
                />
              }
            />

            {createFormatsModal()}

            {isCardView ? (
              <VerticalSpacer size={scaling.icons.spacerBetween + 8} />
            ) : (
              <HorizontalSpacer size={scaling.icons.spacerBetween * 3} />
            )}
          </>
        )}

        {(!purchased && !onReadonlyPage && !rpc_guid && job_status === JOB_STATUSES.FAILED) && (
            <>
                <IconButton
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    width={scaling.icons.width}
                    height={scaling.icons.height}
                    onClick={e => {
                        e.stopPropagation();
                        setConfirmReprocess(true);
                    }}
                    icon={
                        <RefreshOutlined
                            color={theme.colors[selectedTheme][iconColor]}
                            hoverColor={theme.colors[selectedTheme].accent}
                            width={scaling.icons.width}
                            height={scaling.icons.height}
                        />
                    }
                />
                {isCardView ? (
                  <VerticalSpacer size={scaling.icons.spacerBetween + 8} />
                ) : (
                  <HorizontalSpacer size={scaling.icons.spacerBetween * 3} />
                )}
            </>
        )}

        {(!purchased && !onReadonlyPage || hasAccess(userRoles, [ 'creator' ])) && (
          <IconButton
            theme={theme}
            $selectedTheme={selectedTheme}
            width={scaling.icons.width}
            height={scaling.icons.height}
            onClick={e => {
              e.stopPropagation();
              setConfirmDelete(true);
            }}
            icon={
              <DeleteOutlined
                color={theme.colors[selectedTheme][iconColor]}
                hoverColor={theme.colors[selectedTheme].accent}
                width={scaling.icons.width}
                height={scaling.icons.height}
              />
            }
          />
        )}
      </ButtonsContainer>

      <FovConfirmation
        onConfirm={handleDeleting}
        onClose={() => setConfirmDelete(false)}
        confirmationText='Delete'
        isOpened={confirmDelete}
        confirmColor={errorColor}
        confirmHoverColor={errorHoveredColor}
      >
        <Body size='Medium'>
          {`Are you sure you want to delete ${title || 'selected model'}?`}
        </Body>
      </FovConfirmation>

      <FovConfirmation
        onConfirm={handleReprocessing}
        onClose={() => setConfirmReprocess(false)}
        confirmationText='Reprocess'
        isOpened={confirmReprocess}
      >
        <Body size='Medium'>
            {`Are you sure you want to reprocess ${title || 'selected model'}?`}
        </Body>
      </FovConfirmation>

    </>
  );
});

export { ModelActions, handleStatus };
